import { FC, useCallback } from 'react';
import { useFragment, useMutation } from 'react-relay';
import { Formik } from 'formik';
import graphql from 'babel-plugin-relay/macro';
import * as yup from 'yup';

import Form from 'components/Form';
import AccordionController from 'components/AccordionController';
import General from 'components/PackageEditMainGeneral';
import Price from 'components/PackageEditMainPrice';
import Presentation from 'components/PackageEditMainPresentation';
import Terms from 'components/PackageEditMainTerms';
import Documents from 'components/PackageEditMainDocuments';

import { stringToNumber, textFieldValue, useErrorHandler } from 'lib';

import { SelectValue } from 'components/Select';
import { AutocompleteOption } from 'components/Autocomplete';
import { useCurrentUser } from 'components/CurrentUserProvider';

import {
  MarkupType,
  PackageEditMain_package$key,
  PackageStatus,
} from '__generated__/PackageEditMain_package.graphql';
import { PackageEditMainFormMutation } from '__generated__/PackageEditMainFormMutation.graphql';
import { PackageEditMain_data$key } from '__generated__/PackageEditMain_data.graphql';

export type FormValues = {
  name: string;
  status: PackageStatus;
  subtitle: string;
  city: AutocompleteOption | null;
  categories: readonly SelectValue[];
  labels: readonly SelectValue[];
  markupType: MarkupType | null;
  markupValueAdult: number | null;
  markupValueChild: number | null;
  validBeforeTripStartDays: string;
  remainingPaymentDeadlineDays: string;
  individualAdvance: string;
  alreadyPurchased: string;
  descriptionTitle: string;
  descriptionText: string;
  notesTitle: string;
  notesText: string;
  organiser: string;
  whatToSee: AutocompleteOption | null;
  included: readonly SelectValue[];
  excluded: readonly SelectValue[];
  documents: readonly SelectValue[];
};

type Props = {
  package: PackageEditMain_package$key;
  data: PackageEditMain_data$key;
};

const schema = yup.object({
  city: yup
    .object()
    .nullable()
    .when('status', {
      is: 'PUBLISHED',
      then: schema =>
        schema.required(
          'Norėdami publikuoti pasiūlymą prašome pasirinkti kelionės miestą'
        ),
      otherwise: schema => schema,
    }),
});

const PackageEditMain: FC<Props> = props => {
  const errorHandler = useErrorHandler();
  const { itemsPerPage } = useCurrentUser();

  const pack = useFragment(
    graphql`
      fragment PackageEditMain_package on Package {
        id
        name
        status
        subtitle
        markupType
        markupValueAdult
        markupValueChild
        validBeforeTripStartDays
        remainingPaymentDeadlineDays
        individualAdvance
        alreadyPurchased
        descriptionTitle
        descriptionText
        notesTitle
        notesText
        organiser

        city {
          id
          name
        }

        categories {
          id
          name
        }

        labels {
          id
          name
        }

        whatToSee {
          id
          name
        }

        included {
          id
          name
          icon
        }

        excluded {
          id
          name
          icon
        }

        documents {
          id
          name
        }

        # ...PackageWarnings_package
      }
    `,
    props.package
  );

  const data = useFragment(
    graphql`
      fragment PackageEditMain_data on Query {
        ...PackageEditMainGeneral_data
        ...PackageEditMainPresentation_data
        ...PackageEditMainTerms_data
        ...PackageEditMainDocuments_data
      }
    `,
    props.data
  );

  const [update, loading] = useMutation<PackageEditMainFormMutation>(graphql`
    mutation PackageEditMainFormMutation(
      $first: Int!
      $id: ID!
      $input: UpdatePackageInput!
    ) {
      updatePackage(id: $id, input: $input) {
        ...PackageEditTabs_package
        ...PackageSidebar_package
      }
    }
  `);

  const initial: FormValues = {
    name: pack.name,
    status: pack.status,
    subtitle: pack.subtitle ?? '',
    city: pack.city,
    categories: pack.categories,
    labels: pack.labels,
    markupType: pack.markupType,
    markupValueAdult: pack.markupValueAdult,
    markupValueChild: pack.markupValueChild,
    validBeforeTripStartDays: textFieldValue(pack.validBeforeTripStartDays),
    remainingPaymentDeadlineDays: textFieldValue(
      pack.remainingPaymentDeadlineDays
    ),
    individualAdvance: pack.individualAdvance?.toString() ?? '',
    alreadyPurchased: pack.alreadyPurchased?.toString() ?? '',
    descriptionTitle: pack.descriptionTitle ?? '',
    descriptionText: pack.descriptionText ?? '',
    notesTitle: pack.notesTitle ?? '',
    notesText: pack.notesText ?? '',
    organiser: pack.organiser ?? '',
    whatToSee: pack.whatToSee,
    included: pack.included,
    excluded: pack.excluded,
    documents: pack.documents,
  };

  const handleSubmit = useCallback(
    (values: FormValues) => {
      update({
        variables: {
          id: pack.id,
          first: itemsPerPage,
          input: {
            name: values.name,
            subtitle: values.subtitle,
            status: values.status,
            markupType: values.markupType,
            markupValueAdult: values.markupValueAdult,
            markupValueChild: values.markupValueChild,
            validBeforeTripStartDays: stringToNumber(
              values.validBeforeTripStartDays
            ),
            remainingPaymentDeadlineDays: stringToNumber(
              values.remainingPaymentDeadlineDays
            ),
            individualAdvance: stringToNumber(values.individualAdvance),
            alreadyPurchased: stringToNumber(values.alreadyPurchased),
            notesTitle: values.notesTitle,
            notesText: values.notesText,
            descriptionTitle: values.descriptionTitle,
            descriptionText: values.descriptionText,
            organiser: values.organiser,
            city: values.city
              ? {
                  id: values.city.id,
                }
              : null,
            categories: values.categories.map(({ id }) => ({ id })),
            labels: values.labels.map(({ id }) => ({ id })),
            whatToSee: values.whatToSee
              ? {
                  id: values.whatToSee.id,
                }
              : null,
            terms: [
              ...values.included.map(({ id }) => ({ id, included: true })),
              ...values.excluded.map(({ id }) => ({ id, included: false })),
            ],
            documents: values.documents.map(({ id }) => ({ id })),
          },
        },

        ...errorHandler({
          successMessage: 'Informacija išsaugota',
        }),
      });
    },
    [errorHandler, itemsPerPage, pack.id, update]
  );

  return (
    <Formik<FormValues>
      initialValues={initial}
      onSubmit={handleSubmit}
      validationSchema={schema}>
      <Form loading={loading}>
        {/* <PackageWarnings package={pack} /> */}

        <AccordionController id="packageMain">
          <General data={data} />
          <Price />
          <Presentation data={data} />
          <Terms data={data} />
          <Documents data={data} />
        </AccordionController>
      </Form>
    </Formik>
  );
};

export default PackageEditMain;
